import * as React from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'

import { cn } from '@/lib/utils'
import { useSnapDismiss } from '@/components/ui/drawer-snap-dismiss'
import { useSheetTravel } from '@/components/ui/drawer-travel'

// A bottom sheet — the mobile surface for a menu whose items are ACTIONS, where a dropdown anchored to a
// top-corner avatar puts a dozen small targets at the far end of the screen from the thumb.
//
// The DISMISS DRAG is a native scroll (drawer-snap-dismiss.ts) and OPEN/CLOSE is a pre-integrated spring handed
// to the compositor (drawer-travel.ts), so neither can be stuttered by a React commit. Accessibility, focus trap,
// scroll lock, Escape and mount-until-exit-ends are base-ui Dialog's; only the motion is ours.

/** How far the scroller extends BELOW the viewport, so the spring's overshoot cannot show the page through. */
const BLEED = '-bottom-12' // 3rem, paid back as the panel's bottom padding

/** The REF is the context value (not a closure over it), so the provider never publishes a fresh value. */
const DrawerActionsContext = React.createContext<React.RefObject<DialogPrimitive.Root.Actions | null> | null>(null)

/** The sheet's own open state, for the parts that must act on the CLOSE while base-ui keeps the popup mounted. */
const DrawerOpenContext = React.createContext(false)

function Drawer({ open, onOpenChange, ...props }: Omit<DialogPrimitive.Root.Props, 'actionsRef'>) {
  const actionsRef = React.useRef<DialogPrimitive.Root.Actions | null>(null)
  // Mirrored so the travel works for an uncontrolled sheet too: base-ui reports every change either way.
  const [selfOpen, setSelfOpen] = React.useState(props.defaultOpen ?? false)
  return (
    <DrawerActionsContext.Provider value={actionsRef}>
      <DrawerOpenContext.Provider value={open ?? selfOpen}>
        <DialogPrimitive.Root
          actionsRef={actionsRef}
          open={open}
          onOpenChange={(nextOpen, details) => {
            setSelfOpen(nextOpen)
            onOpenChange?.(nextOpen, details)
          }}
          {...props}
        />
      </DrawerOpenContext.Provider>
    </DrawerActionsContext.Provider>
  )
}

function DrawerTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="drawer-trigger" {...props} />
}

function DrawerClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="drawer-close" {...props} />
}

interface DrawerContentProps {
  children?: React.ReactNode
  /** Classes for the visible PANEL — the sheet itself. The scroller around it is the mechanism, not a surface. */
  className?: string
  /** The grabber. Drop it only where something else already signals the sheet can be dragged away. */
  showHandle?: boolean
}

function DrawerContent(props: DrawerContentProps) {
  // A child of the Portal so it MOUNTS WITH THE SHEET: its refs are live before the first paint, which is what lets
  // the scroller be parked on the panel rather than on the dismissed slot.
  return (
    <DialogPrimitive.Portal>
      <DrawerSurface {...props} />
    </DialogPrimitive.Portal>
  )
}

function DrawerSurface({ className, children, showHandle = true }: DrawerContentProps) {
  const scrollerRef = React.useRef<HTMLDivElement | null>(null)
  const backdropRef = React.useRef<HTMLDivElement | null>(null)
  const panelRef = React.useRef<HTMLDivElement | null>(null)
  const handleRef = React.useRef<HTMLDivElement | null>(null)
  const actions = React.useContext(DrawerActionsContext)
  const open = React.useContext(DrawerOpenContext)
  const dismiss = () => actions?.current?.close()
  useSheetTravel({ scrollerRef, backdropRef, panelRef, handleRef, open, onDismiss: dismiss })
  useSnapDismiss({ scrollerRef, backdropRef, onDismiss: dismiss })

  return (
    <>
      <DialogPrimitive.Backdrop
        ref={backdropRef}
        data-slot="drawer-backdrop"
        className={cn(
          'bg-black/10 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 z-50 min-h-dvh',
          // While the finger owns the sheet the fade must TRACK it, not chase it through a transition.
          // `--sheet-dismiss` is written by the scroll read-back. (Open/close is animated — see useSheetTravel.)
          'opacity-[calc(1-var(--sheet-dismiss,0))] transition-opacity duration-100 data-scrolling:duration-0',
          // A dialog opened FROM the sheet renders its own backdrop over everything, so this one steps aside: two
          // full-screen blurs is both double dim and the most expensive thing on the page. The BLUR must be dropped
          // by name — `opacity: 0` does not hide a backdrop-filter, which WebKit paints regardless.
          '[&:has(~[data-nested-dialog-open])]:opacity-0',
          '[&:has(~[data-nested-dialog-open])]:[--tw-backdrop-blur:blur(0px)]',
        )}
      />
      <DialogPrimitive.Popup
        ref={scrollerRef}
        // base-ui hides the background with `aria-hidden` but sets no `aria-modal`; the APG asks for both.
        aria-modal
        data-slot="drawer-content"
        initialFocus={panelRef}
        className={cn(
          // THE SCROLLER: two snap slots, parked on the second one. Taller than the viewport by the bleed.
          'snap-y snap-mandatory overscroll-y-none fixed inset-x-0 top-0 z-50 overflow-y-auto outline-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          BLEED,
          // Its transform is the open/close travel's, written by the compositor — never by a CSS transition.
          'will-change-transform',
        )}
      >
        {/* The empty slot: what the sheet scrolls away INTO, and the tap target that dismisses it (it covers the
            page, so an "outside press" never leaves the popup). */}
        {/* `aria-hidden`: a redundant press target — Escape and the swipe are the ones a keyboard has. */}
        <div aria-hidden data-slot="drawer-dismiss-area" className="h-full w-full snap-start" onClick={dismiss} />
        <div
          ref={panelRef}
          tabIndex={-1}
          data-slot="drawer-panel"
          // No `overscroll-contain` on purpose: at the top of its own scroll a downward drag must chain OUT to the snap
          // scroller — that chaining is the dismiss gesture. `px-2` pairs with the rows' own, so icons align.
          className={cn(
            'bg-background ring-foreground/10 mx-auto flex max-h-[calc(85dvh+3rem)] w-full snap-end flex-col overflow-y-auto rounded-t-2xl bg-clip-padding px-2 pt-2 pb-[calc(1rem+env(safe-area-inset-bottom,0px)+3rem)] text-sm ring-1 outline-none',
            className,
          )}
        >
          <div data-slot="drawer-body" className="flex flex-col gap-1">
            {showHandle && (
              // The grab STRIP, not just the pill: 4px is a fine thumb target but an unusable mouse one, and the
              // mouse is the only pointer that needs to grab the handle itself.
              <div
                ref={handleRef}
                aria-hidden
                data-slot="drawer-handle"
                className="-mt-2 mb-1 flex w-full shrink-0 cursor-grab justify-center py-2 active:cursor-grabbing"
              >
                <div className="bg-muted-foreground/30 h-1 w-10 rounded-full" />
              </div>
            )}
            {children}
          </div>
        </div>
      </DialogPrimitive.Popup>
    </>
  )
}

function DrawerTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="drawer-title"
      className={cn('text-foreground text-base font-medium', className)}
      {...props}
    />
  )
}

function DrawerDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="drawer-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

function DrawerSeparator({ className, ...props }: React.ComponentProps<'div'>) {
  // Full-bleed: `-mx-2` cancels the panel's inset, so a divider reaches both edges like a native sheet's.
  return <div data-slot="drawer-separator" className={cn('bg-border -mx-2 my-1 h-px shrink-0', className)} {...props} />
}

/** Prop-compatible with `DropdownMenuItem`, so one menu body renders into either surface. Sized for a thumb (44px). */
function DrawerItem({
  className,
  closeOnClick = true,
  disabled,
  onClick,
  render,
  ...props
}: useRender.ComponentProps<'button'> & { closeOnClick?: boolean }) {
  const actions = React.useContext(DrawerActionsContext)
  const ownProps: React.ButtonHTMLAttributes<HTMLButtonElement> & { [key: `data-${string}`]: unknown } = {
    'data-slot': 'drawer-item',
    'data-disabled': disabled || undefined,
    'aria-disabled': disabled || undefined,
    'className': cn(
      "active:bg-muted flex min-h-11 w-full shrink-0 items-center gap-3 rounded-lg px-2 py-2.5 text-left text-sm outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      className,
    ),
    'onClick': (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return
      onClick?.(event)
      if (closeOnClick) actions?.current?.close()
    },
  }
  return useRender({
    defaultTagName: 'button',
    props: mergeProps<'button'>(ownProps, props),
    render,
  })
}

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerItem,
  DrawerSeparator,
  DrawerTitle,
  DrawerTrigger,
}
